import React, { useState, useEffect, useRef } from "react";
import useBreakPoinst from "@hooks/useBreakPoinst";
import {
  PaymentSuccessPaymentContainer,
  PaymentSuccessPaymentTitle,
  PaymentSuccessPaymentRow,
  PaymentSuccessPaymentLabel,
  PaymentSuccessPaymentValue,
  PaymentSuccessPaymentTotal,
  PaymentSuccessPaymentDivider,
  CheckoutContainer,
  PaymentSummaryContainer,
  PaymentMethodsSection,
  PaymentMethodsTitle,
  PaymentMethodsList,
  PaymentFormSection,
  PaymentFormTitle,
  PaymentFormContainer,
  PaymentTitle,
  CreditCardIconContainer,
  ApplePayIconContainer,
  BackButtonContainer,
  BackButtonText,
  StyledArrowBackIcon,
  StyledDivider,
} from "./styles";
import ApplePay from "@assets/svg/ApplePay.svg?react";
import CreditCard from "@assets/svg/CreditCard.svg?react";
import PaymentMethodItem from "./PaymentMethodItem";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useWelcomeBook } from "@services";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import { Divider } from "@mui/material";
const mode = import.meta.env.VITE_MODE ?? "not-found";
const isProd = mode == "PROD";
const base = isProd ? "https://app.msool.io/" : "https://staging.msool.io/";

// Extend Window interface for Moyasar
declare global {
  interface Window {
    Moyasar: any;
  }
}

const GuestPortalCheckout = () => {
  const { isPhone } = useBreakPoinst();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(1);
  const { id, paymentDescription } = useParams();
  const navigate = useNavigate();
  const { GetWelcomeBookQuery, savePayment } = useWelcomeBook();
  const { data } = GetWelcomeBookQuery(id ?? "");
  const result = data?.data?.result;

  // Set Arabic locale for Day.js
  dayjs.locale("ar");

  // Format dates for Arabic display
  const formatArabicDate = (dateString: string) => {
    if (!dateString) return "";
    return dayjs(dateString).format("D MMMM");
  };

  // Calculate number of nights between check-in and check-out
  const calculateNights = () => {
    if (!result?.checkInDate || !result?.checkOutDate) return 0;
    const checkIn = dayjs(result.checkInDate);
    const checkOut = dayjs(result.checkOutDate);
    return checkOut.diff(checkIn, "day");
  };

  const nightsCount = calculateNights();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRTL = language === "ar";
  // Payment form state
  const [price] = useState(result?.totalPrice); // Total price in USD
  const [paymentId] = useState("RES_" + Date.now()); // Generate unique payment ID

  // Load Moyasar script
  const script = document.createElement("script");
  script.src = "https://cdn.jsdelivr.net/npm/moyasar-payment-form@2.2.2/dist/moyasar.umd.min.js";
  script.async = true;
  script.defer = true;
  script.id = "script_id";

  useEffect(() => {
    document.body.append(script);
  }, []);

  // Mock save payment API function
  const savePaymentApi = async (payment: any) => {
    console.log("Payment completed:", payment);
    // Implement actual payment saving logic here
  };

  const RenderPaymentForm = ({ methods = [] }: { methods: string[] }) => {
    const formContainer = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (formContainer.current && window.Moyasar) {
        window.Moyasar.init({
          element: formContainer.current,
          amount: result?.totalPrice * 100,
          currency: "SAR",
          description: paymentDescription,
          publishable_api_key: result?.publicKey, // from get payment api
          callback_url: `${base}guest/payment-status/${id}`, // from save reservation api  ( data )
          methods,
          apple_pay: {
            country: "SA",
            label: "Awesome Cookie Store",
            validate_merchant_url:
              "https://api.moyasar.com/v1/applepay/initiate",
          },
          //  on_completed: 'https://msoolguestapi.msool.io/api/Reservation/SavePayment',
          on_completed: async function (payment: any) {
            await savePayment(payment);
          },
        });
      }
    }, [methods, price, paymentId]);

    return <div className="mysr-form" ref={formContainer} />;
  };

  const paymentMethods = [
    {
      id: 1,
      title: "بطاقة الائتمان/الخصم",
      description: "الدفع بواسطة فيزا، ماستركارد، إلخ.",
      icon: (
        <CreditCardIconContainer>
          <CreditCard width={24} height={24} />
        </CreditCardIconContainer>
      ),
      disabled: false,
    },
    {
      id: 2,
      title: "آبل باي",
      description: "دفع سريع وسلس",
      icon: (
        <ApplePayIconContainer>
          <ApplePay width={24} height={24} />
        </ApplePayIconContainer>
      ),
      disabled: false,
    },
  ];

  if (!result) return;
  return (
    <CheckoutContainer>
      <BackButtonContainer>
        <StyledArrowBackIcon
          $isRTL={isRTL}
          onClick={() => {
            navigate(`/guest/summary-page/${id}`);
          }}
        />
        <BackButtonText>الرجوع</BackButtonText>
      </BackButtonContainer>
      <PaymentSummaryContainer>
        <PaymentSuccessPaymentContainer $isPhone={isPhone}>
          <PaymentSuccessPaymentTitle $isPhone={isPhone}>
            <PaymentTitle $isPhone={isPhone}>الدفع</PaymentTitle>
          </PaymentSuccessPaymentTitle>
          <PaymentSuccessPaymentRow>
            <PaymentSuccessPaymentLabel $isPhone={isPhone}>
              {nightsCount} ليالي
            </PaymentSuccessPaymentLabel>
            <PaymentSuccessPaymentValue $isPhone={isPhone}>
              {result?.totalPrice} ريال
            </PaymentSuccessPaymentValue>
          </PaymentSuccessPaymentRow>
          <PaymentSuccessPaymentDivider />
          <PaymentSuccessPaymentRow>
            <PaymentSuccessPaymentTotal $isPhone={isPhone}>
              السعر الإجمالي للحجز
            </PaymentSuccessPaymentTotal>
            <PaymentSuccessPaymentTotal $isPhone={isPhone}>
              {result?.totalPrice} ريال
            </PaymentSuccessPaymentTotal>
          </PaymentSuccessPaymentRow>
        </PaymentSuccessPaymentContainer>
      </PaymentSummaryContainer>
      <Divider sx={{ backgroundColor: "#E0E0E0", marginBlock: "20px" }} />
      <PaymentMethodsSection>
        <PaymentMethodsTitle>طريقة الدفع</PaymentMethodsTitle>
        <PaymentMethodsList>
          {paymentMethods?.map((paymentMethod) => (
            <PaymentMethodItem
              key={paymentMethod.id}
              paymentMethod={paymentMethod}
              selectedPaymentMethod={selectedPaymentMethod}
              onSelect={setSelectedPaymentMethod}
            />
          ))}
        </PaymentMethodsList>
      </PaymentMethodsSection>

      {/* Payment Form - Only show when credit card is selected (id: 1) */}
      {selectedPaymentMethod === 1 && (
        <PaymentFormSection>
          <Divider sx={{ backgroundColor: "#E0E0E0", marginBlock: "20px" }} />
          <PaymentFormContainer>
            <PaymentFormTitle>تفاصيل الدفع</PaymentFormTitle>
            <RenderPaymentForm methods={["creditcard"]} />
          </PaymentFormContainer>
        </PaymentFormSection>
      )}
      {selectedPaymentMethod === 2 && (
        <PaymentFormSection>
          <Divider sx={{ backgroundColor: "#E0E0E0", marginBlock: "20px" }} />
          <PaymentFormContainer>
            <PaymentFormTitle>تفاصيل الدفع</PaymentFormTitle>
            <RenderPaymentForm methods={["applepay"]} />
          </PaymentFormContainer>
        </PaymentFormSection>
      )}
    </CheckoutContainer>
  );
};

export default GuestPortalCheckout;
